import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useScopeParams } from "./useScopeParams";
import { useWorkspacesQuery } from "./useWorkspacesQuery";
import { useWorkspaceMutations } from "./useWorkspaceMutations";
import {
  TableState,
  ColumnVisibility,
  ColumnsType,
} from "@/types/tableState.types";
import { useUser } from "@/contexts/UserContext";
import { getDefaultViewKey } from "@/utils/tableViews/defaultViewKeys";
import { WorkspaceConfig } from "@/types/workspaceConfig";
import {
  workspaceConfigToTableState,
  createDefaultTableState,
} from "@/utils/workspaceState";
import {
  normalizeWorkspaceConfig,
  workspaceConfigNeedsMigration,
} from "@/utils/workspaceConfig";
import { isSingleSubjectPage } from "@/utils/workspaceUtils";
import {
  getLegacyWorkspaceSelectionStorageKey,
  getOlderLegacyWorkspaceSelectionStorageKey,
  getDataSectionWorkspaceSelectionStorageKey,
  getWorkspaceSelectionStorageKey,
  getWorkspaceUserStorageKey,
  readMigratedLocalStorageItem,
} from "@/utils/workspacePersistence";

const savedViewConfigModules = import.meta.glob<{
  default: unknown;
}>("@/utils/tableViews/files/*.json", {
  eager: true,
});

const savedViewConfigMap: Record<string, unknown> = Object.entries(
  savedViewConfigModules,
).reduce<Record<string, unknown>>((acc, [path, module]) => {
  const configName = path.split("/").pop()?.replace(".json", "") || "";
  if (configName && module?.default) {
    acc[configName] = module.default;
  }
  return acc;
}, {});

function getDefaultWorkspaceConfig(
  trialId: string,
  tableName: string,
  metadataColumns?: string[],
  defaultHiddenColumns?: string[],
): WorkspaceConfig {
  const savedConfig = savedViewConfigMap[getDefaultViewKey(trialId, tableName)];

  if (savedConfig) {
    return savedConfig as WorkspaceConfig;
  }

  const hiddenSet = new Set(defaultHiddenColumns ?? []);
  const defaultColumns: ColumnsType[] =
    metadataColumns?.map((col) => ({
      fieldName: col,
      visibility: hiddenSet.has(col)
        ? ColumnVisibility.HIDDEN
        : ColumnVisibility.VISIBLE,
      mandatory: false,
    })) || [];

  return {
    global: { filters: {} },
    tabs: [
      {
        name: "Table",
        type: "table",
        config: {
          columns: defaultColumns,
          lockedColumns: 0,
          sorting: [],
          pagination: { current: 1, pageSize: 100 },
        },
      },
      {
        name: "Dashboard",
        type: "dashboard",
        config: [],
      },
    ],
    version: "2.0.0",
  };
}

export const useCurrentWorkspace = (
  tableName: string,
  metadata?: {
    // Callers supply either the demo screens' rich column descriptors or, from
    // dev's dashboard templates, plain field-name strings.
    columns?: (string | { field: string; label: string; hidden?: boolean })[];
    filters?: Record<string, unknown>;
  },
  dataSection: string = "all",
  isSingleSubject?: boolean,
) => {
  // tableName also keys workspace storage and the workspaces API, so pages that
  // pin a subject without encoding it in the name pass the flag explicitly
  // rather than renaming the table.
  const isSSJ = isSingleSubject ?? isSingleSubjectPage(tableName);
  const metadataColumns = metadata?.columns?.map((c) =>
    typeof c === "string" ? c : c.field,
  );
  const defaultHiddenColumns = metadata?.columns
    ?.filter((c) => c.hidden)
    .map((c) => c.field);
  const metadataFilterKeys = Object.keys(metadata?.filters || {});

  const { scope, id } = useScopeParams();
  const isAdminScope = scope === "admin";
  const hasWorkspaceContext = isAdminScope || !!id;
  const { userInfo } = useUser();
  const { workspaces, isLoading, error, refetch } = useWorkspacesQuery(
    tableName,
    dataSection,
  );
  const mutations = useWorkspaceMutations(tableName, dataSection);
  const dataSectionWorkspaces = useMemo(() => {
    return workspaces.filter(
      (workspace) => (workspace.data_section ?? "all") === dataSection,
    );
  }, [dataSection, workspaces]);

  const userKey = getWorkspaceUserStorageKey(userInfo);

  const workspaceSelectionKey =
    dataSection === "all"
      ? getWorkspaceSelectionStorageKey(id, tableName, userKey)
      : getDataSectionWorkspaceSelectionStorageKey(
          id,
          tableName,
          dataSection,
          userKey,
        );
  const legacyWorkspaceSelectionKey =
    dataSection === "all"
      ? getLegacyWorkspaceSelectionStorageKey(tableName, userKey)
      : undefined;
  const olderLegacyWorkspaceSelectionKey =
    dataSection === "all"
      ? getOlderLegacyWorkspaceSelectionStorageKey(id, tableName)
      : undefined;

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | null>(
    () => {
      const saved = readMigratedLocalStorageItem(
        workspaceSelectionKey,
        legacyWorkspaceSelectionKey ?? [],
        userKey === "unknown-user"
          ? undefined
          : olderLegacyWorkspaceSelectionKey,
      );
      if (!saved) return null;

      const parsedWorkspaceId = Number.parseInt(saved, 10);
      return parsedWorkspaceId > 0 ? parsedWorkspaceId : null;
    },
  );
  const [
    workspaceSelectionHydratedForKey,
    setWorkspaceSelectionHydratedForKey,
  ] = useState<string | null>(null);

  const persistWorkspaceId = useCallback(
    (workspaceId: number | null) => {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setSelectedWorkspaceId(workspaceId);
      if (workspaceId === null) {
        localStorage.removeItem(workspaceSelectionKey);
      } else {
        localStorage.setItem(workspaceSelectionKey, String(workspaceId));
      }
    },
    [workspaceSelectionKey],
  );

  useEffect(() => {
    const saved = readMigratedLocalStorageItem(
      workspaceSelectionKey,
      legacyWorkspaceSelectionKey ?? [],
      userKey === "unknown-user" ? undefined : olderLegacyWorkspaceSelectionKey,
    );

    if (!saved) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setSelectedWorkspaceId(null);
    } else {
      const parsedWorkspaceId = Number.parseInt(saved, 10);
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setSelectedWorkspaceId(parsedWorkspaceId > 0 ? parsedWorkspaceId : null);
    }

    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setWorkspaceSelectionHydratedForKey(workspaceSelectionKey);
  }, [
    workspaceSelectionKey,
    legacyWorkspaceSelectionKey,
    olderLegacyWorkspaceSelectionKey,
    userKey,
  ]);

  const defaultWorkspaceAttemptedRef = useRef(false);

  useEffect(() => {
    defaultWorkspaceAttemptedRef.current = false;
  }, [id, tableName, dataSection]);

  useEffect(() => {
    if (
      !hasWorkspaceContext ||
      isLoading ||
      defaultWorkspaceAttemptedRef.current ||
      workspaceSelectionHydratedForKey !== workspaceSelectionKey
    )
      return;
    if (!metadataColumns || metadataColumns.length === 0) return;

    if (dataSectionWorkspaces.length === 0 && !error) {
      defaultWorkspaceAttemptedRef.current = true;
      const defaultConfig = getDefaultWorkspaceConfig(
        id,
        tableName,
        metadataColumns,
        defaultHiddenColumns,
      );

      mutations
        .createWorkspaceAsync({
          title: "Default Workspace",
          table_name: tableName,
          is_vivo_template: true,
          configuration: defaultConfig,
        })
        .then((newWorkspace) => {
          persistWorkspaceId(newWorkspace.id);
        })
        .catch((err) => {
          console.error("Failed to create default workspace:", err);
          const status = (err as { status?: number }).status;
          if (status !== 403) {
            defaultWorkspaceAttemptedRef.current = false;
          }
        });
    }
  }, [
    hasWorkspaceContext,
    id,
    tableName,
    dataSectionWorkspaces.length,
    isLoading,
    error,
    metadataColumns,
    defaultHiddenColumns,
    mutations,
    persistWorkspaceId,
    workspaceSelectionHydratedForKey,
    workspaceSelectionKey,
  ]);

  useEffect(() => {
    if (
      workspaceSelectionHydratedForKey !== workspaceSelectionKey ||
      isLoading ||
      dataSectionWorkspaces.length === 0 ||
      selectedWorkspaceId !== null
    )
      return;

    const vivoTemplate = dataSectionWorkspaces.find((w) => w.is_vivo_template);
    const firstWorkspace = vivoTemplate || dataSectionWorkspaces[0];

    if (firstWorkspace) {
      persistWorkspaceId(firstWorkspace.id);
    }
  }, [
    isLoading,
    dataSectionWorkspaces,
    selectedWorkspaceId,
    persistWorkspaceId,
    workspaceSelectionHydratedForKey,
    workspaceSelectionKey,
  ]);

  useEffect(() => {
    if (
      workspaceSelectionHydratedForKey !== workspaceSelectionKey ||
      selectedWorkspaceId === null ||
      dataSectionWorkspaces.length === 0 ||
      dataSectionWorkspaces.some((w) => w.id === selectedWorkspaceId)
    )
      return;

    const vivoTemplate = dataSectionWorkspaces.find((w) => w.is_vivo_template);
    const firstWorkspace = vivoTemplate || dataSectionWorkspaces[0];
    if (firstWorkspace) {
      persistWorkspaceId(firstWorkspace.id);
    }
  }, [
    selectedWorkspaceId,
    dataSectionWorkspaces,
    persistWorkspaceId,
    workspaceSelectionHydratedForKey,
    workspaceSelectionKey,
  ]);

  const currentWorkspace = useMemo(() => {
    if (selectedWorkspaceId === null) return null;
    return (
      dataSectionWorkspaces.find((w) => w.id === selectedWorkspaceId) || null
    );
  }, [dataSectionWorkspaces, selectedWorkspaceId]);

  // Silently migrates a workspace's configuration to the current canonical
  // shape the first time it's viewed in a non-canonical (e.g. legacy) form,
  // so subsequent reads/writes everywhere else can assume the canonical
  // shape. Deduped per distinct normalized config so it fires once, not on
  // every re-render.
  const migratedWorkspaceConfigRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentWorkspace) return;
    const { configuration, id: workspaceId } = currentWorkspace;
    if (!workspaceConfigNeedsMigration(configuration)) return;

    const normalizedConfig = normalizeWorkspaceConfig(configuration);
    const migrationKey = `${workspaceId}:${JSON.stringify(normalizedConfig)}`;
    if (migratedWorkspaceConfigRef.current === migrationKey) return;
    migratedWorkspaceConfigRef.current = migrationKey;

    mutations
      .updateWorkspaceAsync({
        workspaceId,
        payload: { configuration: normalizedConfig },
      })
      .catch((err) => {
        console.error("Failed to migrate workspace configuration:", err);
        migratedWorkspaceConfigRef.current = null;
      });
  }, [currentWorkspace, mutations]);

  const setCurrentWorkspace = useCallback(
    (workspaceId: number) => {
      persistWorkspaceId(workspaceId);
    },
    [persistWorkspaceId],
  );

  const initialTableState = useMemo((): TableState => {
    const normalizedConfiguration = currentWorkspace?.configuration;

    if (normalizedConfiguration) {
      const tableState = workspaceConfigToTableState(
        normalizedConfiguration,
        metadataColumns,
        metadataFilterKeys,
      );

      if (isSSJ && tableState.filters?.subject) {
        /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
        const { subject, ...otherFilters } = tableState.filters;
        tableState.filters = otherFilters;
      }

      return {
        ...tableState,
        workspaceId: currentWorkspace.id,
        workspaceModifiedAt: currentWorkspace.modified_at,
      };
    }
    return createDefaultTableState(metadataColumns, defaultHiddenColumns);
  }, [
    currentWorkspace,
    isSSJ,
    metadataColumns,
    defaultHiddenColumns,
    metadataFilterKeys,
  ]);

  return {
    currentWorkspace,
    setCurrentWorkspace,
    workspaces: dataSectionWorkspaces,
    isLoading,
    error,
    refetch,
    initialTableState,
    mutations,
  };
};
